import { ActivatedRouteSnapshot, Data, RouterStateSnapshot } from '@angular/router';
import { getLastElement } from '../array';
import { isNullOrWhitespace, isWhitespace } from '../string';

export interface RouteWithTitle {
  firstChild: RouteWithTitle | null;
  data?: {
    title?: string;
  };
}

export function getTitle(snapshot: ActivatedRouteSnapshot) {
  while (snapshot.firstChild != null) {
    snapshot = snapshot.firstChild;
  }
  return snapshot.data.title;
}

export function getInnermostChildOrSelf(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  while (route.firstChild != null) {
    route = route.firstChild;
  }
  return route;
}

export function getRoutePath(route: ActivatedRouteSnapshot) {
  route = getInnermostChildOrSelf(route);
  let path = route.pathFromRoot
    .map((r) => r.routeConfig?.path)
    .filter((p) => !isWhitespace(p))
    .join('/');
  path = isWhitespace(path) ? '/' : path;
  return path;
}

export function getTitleSegments(route: RouteWithTitle | null): string[] {
  const titleSegments: string[] = [];
  const pushTitleSegement = (rt: RouteWithTitle | null) => {
    if (
      rt != null &&
      rt.data != null &&
      rt.data.title != null &&
      getLastElement(titleSegments) !== rt.data.title
    ) {
      titleSegments.push(rt.data.title);
    }
  };

  pushTitleSegement(route);
  do {
    route = route == null ? null : route.firstChild;
    pushTitleSegement(route);
  } while (route != null && route.firstChild != null);
  return titleSegments;
}

export function combineTitleSegments(titleSegements: string[]): string {
  const title = titleSegements.reduce(
    (parentTitleSegement, childTitleSegement) =>
      !isNullOrWhitespace(parentTitleSegement)
        ? `${childTitleSegement} - ${parentTitleSegement}`
        : childTitleSegement,
    '',
  );
  return title == null ? '' : title;
}

export function getParamValueFromRouterState(
  state: RouterStateSnapshot,
  paramName: string,
): string | undefined {
  let route = state.root;
  let paramValue: string | undefined = route.params[paramName];
  while (paramValue == null && route.firstChild != null) {
    route = route.firstChild;
    paramValue = route.params[paramName];
  }
  return paramValue;
}

export interface RouteInfo {
  path: string;
  url: string;
  segments: Omit<RouteInfo, 'segments'>[];
  data: Data;
}
/**
 * get basic information based on route snapshot
 * @example
 * return {
 *   path: string, // with param names for each segment
 *   url: string,  // with param values for each segment
 *   data: Data    // data merged from each route data segment
 *   segments:     // list of segments used to build `RouteInfo`
 * }
 */
export function getRouteSnapshotInfo(route: ActivatedRouteSnapshot): RouteInfo {
  const segments: Omit<RouteInfo, 'segments'>[] = [];
  route = getInnermostChildOrSelf(route);
  for (const r of route.pathFromRoot) {
    const segment: { path: string; url: string; data?: any } = {
      path: r.routeConfig?.path ?? '',
      url: r.routeConfig?.path ?? '',
    };
    if (r.routeConfig?.path == null || isWhitespace(r.routeConfig.path)) {
      if (Object.keys(r.data).length > 0) {
        segments.push({ ...segment, data: r.data });
      }
      continue;
    }
    const paramIndex = segment.path.indexOf(':') + 1;
    if (paramIndex > 0 && r.paramMap.keys.length >= paramIndex) {
      const value = r.paramMap.get(segment.path.substring(paramIndex)) ?? segment.url;
      segment.url = value;
    }
    segments.push({
      ...segment,
      data: r.data,
    });
  }
  const getUrl = (val: string) => (isWhitespace(val) ? '/' : val);
  const urlSegments = segments.filter((p) => !isWhitespace(p.url));
  const url = getUrl(urlSegments.map((p) => p.url).join('/'));
  const path = getUrl(urlSegments.map((p) => p.path).join('/'));
  const mergedData = segments.map((s) => s.data).reduce((p, c) => (p = { ...p, ...c }), {});

  return { url, path, data: mergedData, segments };
}
